import { Vector3 } from 'three';

/** A free-form control node. The curve passes through `p`; handles are relative to `p`. */
export interface TrackNode {
  id: string;
  p: Vector3;
  hIn: Vector3; // incoming handle, relative to p
  hOut: Vector3; // outgoing handle, relative to p
  mode: 'smooth' | 'broken';
  /**
   * When set, pin that channel of the node tangent after autosmooth.
   * Unset channels follow the C² spline through neighbouring points.
   */
  forcedYawDeg?: number;
  forcedPitchDeg?: number;
  /** True once the user has authored this node's world position. */
  forcedPos?: boolean;
  /** When set, pin gravity-relative banking at this node (0° = rails level with
   *  the ground). Converted into the track-relative RMF channel when frames are
   *  built, so earlier-piece edits cannot change this authored angle.
   *  Overrides are ignored while the tangent is vertical (gravity bank is undefined).
   *  Interpolation between pins takes the shortest turn (≤180°), so a 360° twist
   *  needs three pins. */
  forcedBankDeg?: number;
}

export interface TrackDef {
  closed: boolean;
  nodes: TrackNode[];
}

let nodeCounter = 0;
export function newNodeId(): string {
  return `n${Date.now().toString(36)}_${(nodeCounter++).toString(36)}`;
}

export function makeNode(
  p: Vector3,
  hIn: Vector3,
  hOut: Vector3,
  mode: 'smooth' | 'broken' = 'smooth',
): TrackNode {
  return { id: newNodeId(), p: p.clone(), hIn: hIn.clone(), hOut: hOut.clone(), mode };
}

/** Number of cubic spans in the chain. */
export function spanCount(track: TrackDef): number {
  const n = track.nodes.length;
  if (n < 2) return 0;
  return track.closed ? n : n - 1;
}

/** The four Bézier control points of span i (from node i to node i+1, wrapping if closed). */
export function spanControlPoints(track: TrackDef, i: number): [Vector3, Vector3, Vector3, Vector3] {
  const n = track.nodes.length;
  const a = track.nodes[i % n];
  const b = track.nodes[(i + 1) % n];
  const p0 = a.p;
  const p1 = a.p.clone().add(a.hOut);
  const p3 = b.p;
  const p2 = b.p.clone().add(b.hIn);
  return [p0, p1, p2, p3];
}

export function evalSpan(cp: [Vector3, Vector3, Vector3, Vector3], u: number, out = new Vector3()): Vector3 {
  const t = u;
  const s = 1 - t;
  const b0 = s * s * s;
  const b1 = 3 * s * s * t;
  const b2 = 3 * s * t * t;
  const b3 = t * t * t;
  out.set(
    b0 * cp[0].x + b1 * cp[1].x + b2 * cp[2].x + b3 * cp[3].x,
    b0 * cp[0].y + b1 * cp[1].y + b2 * cp[2].y + b3 * cp[3].y,
    b0 * cp[0].z + b1 * cp[1].z + b2 * cp[2].z + b3 * cp[3].z,
  );
  return out;
}

/** First derivative with respect to u. */
export function evalSpanDeriv(cp: [Vector3, Vector3, Vector3, Vector3], u: number, out = new Vector3()): Vector3 {
  const t = u;
  const s = 1 - t;
  const c0 = 3 * s * s;
  const c1 = 6 * s * t;
  const c2 = 3 * t * t;
  out.set(
    c0 * (cp[1].x - cp[0].x) + c1 * (cp[2].x - cp[1].x) + c2 * (cp[3].x - cp[2].x),
    c0 * (cp[1].y - cp[0].y) + c1 * (cp[2].y - cp[1].y) + c2 * (cp[3].y - cp[2].y),
    c0 * (cp[1].z - cp[0].z) + c1 * (cp[2].z - cp[1].z) + c2 * (cp[3].z - cp[2].z),
  );
  return out;
}

/** Second derivative with respect to u. */
export function evalSpanSecondDeriv(
  cp: [Vector3, Vector3, Vector3, Vector3],
  u: number,
  out = new Vector3(),
): Vector3 {
  const t = u;
  const s = 1 - t;
  // B''(t) = 6(1-t)(p2-2p1+p0) + 6t(p3-2p2+p1)
  out.set(
    6 * s * (cp[2].x - 2 * cp[1].x + cp[0].x) + 6 * t * (cp[3].x - 2 * cp[2].x + cp[1].x),
    6 * s * (cp[2].y - 2 * cp[1].y + cp[0].y) + 6 * t * (cp[3].y - 2 * cp[2].y + cp[1].y),
    6 * s * (cp[2].z - 2 * cp[1].z + cp[0].z) + 6 * t * (cp[3].z - 2 * cp[2].z + cp[1].z),
  );
  return out;
}

/** Curvature magnitude at parameter u on a span. */
export function spanCurvature(cp: [Vector3, Vector3, Vector3, Vector3], u: number): number {
  const d1 = evalSpanDeriv(cp, u);
  const d2 = evalSpanSecondDeriv(cp, u);
  const cross = new Vector3().crossVectors(d1, d2);
  const speed = d1.length();
  if (speed < 1e-9) return 0;
  return cross.length() / (speed * speed * speed);
}

/** Curvature vector (points toward the centre of the osculating circle), magnitude = curvature. */
export function spanCurvatureVector(
  cp: [Vector3, Vector3, Vector3, Vector3],
  u: number,
  out = new Vector3(),
): Vector3 {
  const d1 = evalSpanDeriv(cp, u);
  const d2 = evalSpanSecondDeriv(cp, u);
  const v2 = d1.lengthSq();
  if (v2 < 1e-12) return out.set(0, 0, 0);
  // κ_vec = (d2 - (d2·d1)/|d1|² d1) / |d1|²   — the normal component of acceleration in arc-length terms
  const proj = d2.dot(d1) / v2;
  out.copy(d2).addScaledVector(d1, -proj).divideScalar(v2);
  return out;
}

/**
 * Split span i at parameter u without changing the curve shape (de Casteljau).
 * Returns the new node to insert between node i and node i+1, plus replacement
 * handles for the surrounding nodes.
 */
export function splitSpan(
  track: TrackDef,
  i: number,
  u: number,
): { newNode: TrackNode; hOutA: Vector3; hInB: Vector3 } {
  const [p0, p1, p2, p3] = spanControlPoints(track, i);
  const q0 = p0.clone().lerp(p1, u);
  const q1 = p1.clone().lerp(p2, u);
  const q2 = p2.clone().lerp(p3, u);
  const r0 = q0.clone().lerp(q1, u);
  const r1 = q1.clone().lerp(q2, u);
  const m = r0.clone().lerp(r1, u);

  const newNode = makeNode(m, r0.sub(m), r1.sub(m), 'smooth');
  return {
    newNode,
    hOutA: q0.sub(p0), // new hOut for node i
    hInB: q2.sub(p3), // new hIn for node i+1
  };
}

/**
 * The locality guarantee of a cubic chain: moving node k only affects the spans
 * that reference node k's position or handles — spans k−1 and k. We return a
 * slightly padded range for mesh chunk rebuilds.
 */
export function dirtySpansForNode(track: TrackDef, nodeIndex: number, pad = 1): number[] {
  const nSpans = spanCount(track);
  if (nSpans === 0) return [];
  const spans: number[] = [];
  const lo = nodeIndex - 1 - pad;
  const hi = nodeIndex + pad;
  for (let i = lo; i <= hi; i++) {
    const idx = track.closed ? ((i % nSpans) + nSpans) % nSpans : i;
    if (idx >= 0 && idx < nSpans && !spans.includes(idx)) spans.push(idx);
  }
  return spans;
}
